import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import Teachr from "../../../Objects/models/Teachr";
import SessionDescriptionRepository, { SESSION_DESCRIPTION_MAX_LENGTH } from "../../../Repositories/SessionDescriptionRepository";
import FormInput from "../../../Components/Ui/FormInput";
import ButtonField from "../../../Components/Ui/ButtonField";
import { useTailwindBrand } from "../../../Components/shared";
import { getActiveBrandConfig, getActivePrestationBrandConfig } from "../../../config/environment.config";
import { sessionDescriptionOverrideError } from "../../../Utils/sessionDescriptionOverride";

interface Props {
    teachr: Teachr | undefined;
    /** Surcharge en cours de saisie pour ce cours (null : description habituelle). */
    value: string | null;
    onChange: (value: string | null) => void;
    /** Description habituelle, remontée au parent pour la normalisation à l'envoi. */
    onGenericLoaded?: (generic: string | null) => void;
}

/**
 * Étape 1 de la déclaration : carte « Déroulement de vos séances ». Rappelle
 * la description habituelle (celle des devis) et permet de la remplacer pour
 * ce cours seulement. Vide, c'est la description habituelle qui s'applique.
 */
const SessionDescriptionCard = ({ teachr, value, onChange, onGenericLoaded }: Props) => {
    const { classes, common, brandConfig } = useTailwindBrand();
    const navigate = useNavigate();
    const location = useLocation();
    const [generic, setGeneric] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [customizing, setCustomizing] = useState(value != null && value !== "");

    useEffect(() => {
        let cancelled = false;
        if (!teachr?.id) {
            setLoading(false);
            return undefined;
        }
        SessionDescriptionRepository.get(teachr.id)
            .then((response) => {
                if (cancelled) return;
                const text = response?.sessionDescription?.description ?? null;
                setGeneric(text);
                onGenericLoaded && onGenericLoaded(text);
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teachr?.id]);

    const startCustomizing = () => {
        setCustomizing(true);
        if (value == null || value === "") onChange(generic ?? "");
    };

    const resetToGeneric = () => {
        setCustomizing(false);
        onChange(null);
    };

    const goToSettings = () => {
        navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, {
            state: { step: 5, returnTo: location.pathname },
        });
    };

    const error = sessionDescriptionOverrideError(value);
    const length = (value ?? "").trim().length;

    return (
        <div className="mb-6 rounded-md border border-gray-200 bg-gray-50 p-4" data-testid="session-description-card">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <DescriptionOutlinedIcon fontSize="small" className={classes.primaryText} />
                    <p className={`${common.body} ${classes.primaryText} font-semibold`}>
                        Déroulement de vos {getActivePrestationBrandConfig("s")}
                    </p>
                </div>
                {!loading && generic && !customizing && (
                    <ButtonField size="small" variant="outlined" label="Personnaliser pour ce cours" onClick={startCustomizing} />
                )}
                {!loading && customizing && (
                    <ButtonField size="small" variant="outlined" label="Reprendre ma description habituelle" onClick={resetToGeneric} />
                )}
            </div>

            {loading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                    <CircularProgress size={14} /> Chargement de votre description...
                </div>
            )}

            {!loading && !generic && (
                <div className="mt-2">
                    <p className={`text-sm text-gray-600 ${classes.fontFamily}`}>
                        Vous n'avez pas encore décrit le déroulement habituel de vos {getActivePrestationBrandConfig("s")}. Cette description est obligatoire pour déclarer et figure sur vos devis.
                    </p>
                    <button type="button" onClick={goToSettings} className={`text-sm underline mt-1 ${classes.primaryText}`}>
                        Renseigner ma description
                    </button>
                </div>
            )}

            {/* la description habituelle est renseignée une fois pour toutes dans les
                paramètres : on ne la répète pas à chaque déclaration */}
            {!loading && generic && !customizing && (
                <p className={`text-xs text-gray-500 mt-2 ${classes.fontFamily}`} data-testid="session-description-generic">
                    Votre description habituelle figurera sur le devis de ce {getActivePrestationBrandConfig()}.{" "}
                    <button type="button" onClick={goToSettings} className={`underline ${classes.primaryText}`}>
                        Modifier ma description habituelle
                    </button>
                </p>
            )}

            {!loading && customizing && (
                <div className="mt-3">
                    <FormInput
                        label={`Déroulement propre à ce ${getActivePrestationBrandConfig()}`}
                        textarea={true}
                        value={value ?? ""}
                        onChangeText={(e: any) => onChange(e.target.value)}
                        placeholder={`Décrivez ce qui sera fait pendant ce ${getActivePrestationBrandConfig()} en particulier`}
                    />
                    <div className="flex justify-between gap-2 mt-1">
                        <p className={`text-xs ${error ? "text-red-600" : "text-gray-500"} ${classes.fontFamily}`}>
                            {error || `Remplace votre description habituelle sur le devis de ce ${getActivePrestationBrandConfig()} uniquement.`}
                        </p>
                        <p className={`text-xs ${length > SESSION_DESCRIPTION_MAX_LENGTH ? "text-red-600" : "text-gray-400"}`} style={{ fontFamily: brandConfig.fontFamily }}>
                            {length}/{SESSION_DESCRIPTION_MAX_LENGTH}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SessionDescriptionCard;
